/**
 * Keeps the "Swedish Premium Upcharge" cart line in sync with the total number of
 * Swedish (Premium) candies across all Pick N Mix lines in the cart — $2.00 each.
 *
 * Each Pick N Mix line carries a hidden "_Premium Count" property (set by the
 * candy-picker block) recording how many Swedish (Premium) candies are in that
 * specific bag. This script is the single source of truth for the upcharge quantity:
 * it recalculates from scratch on every cart change (add, quantity update, remove),
 * so the upcharge line always matches reality — including when a Pick N Mix line's
 * quantity is changed or the line is removed directly in the cart/drawer.
 *
 * The upcharge line itself is locked in the cart UI (see cart-products.liquid) so
 * customers can't edit or remove it directly — only by modifying the Pick N Mix
 * line(s) that generated it.
 */
(function () {
  var PREMIUM_UPCHARGE_VARIANT_ID = 49937691672833;
  var syncing = false;
  var pendingResync = false;

  function premiumCountFor(item) {
    var raw = item.properties && item.properties['_Premium Count'];
    var parsed = parseInt(raw, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  async function recalculate() {
    if (syncing) {
      pendingResync = true;
      return;
    }
    syncing = true;

    try {
      var cart = await fetch('/cart.js').then(function (r) {
        return r.json();
      });

      var needed = 0;
      var currentQty = 0;
      var upchargeLine = null;

      cart.items.forEach(function (item) {
        if (item.variant_id === PREMIUM_UPCHARGE_VARIANT_ID) {
          currentQty = item.quantity;
          upchargeLine = item;
        } else {
          needed += premiumCountFor(item) * item.quantity;
        }
      });

      if (needed === currentQty) return;

      var response;
      if (!upchargeLine) {
        // Not in the cart yet — /cart/add.js takes a variant id.
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: PREMIUM_UPCHARGE_VARIANT_ID, quantity: needed }),
        });
      } else {
        // Already a line in the cart — /cart/change.js requires the line's
        // key (or 1-based position), NOT the variant id, or it 400s.
        response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: upchargeLine.key, quantity: needed }),
        });
      }

      if (!response.ok) {
        var errorBody = await response.json().catch(function () {
          return null;
        });
        console.error('[premium-upcharge-sync] Failed to sync upcharge (' + response.status + ')', errorBody);
        return;
      }

      var refreshEvent = new Event('shopify:cart:lines-update', { bubbles: true });
      refreshEvent.promise = Promise.resolve({ detail: {} });
      refreshEvent.__surchargeSync = true;
      document.dispatchEvent(refreshEvent);
    } catch (error) {
      console.warn('[premium-upcharge-sync] Error while syncing upcharge', error);
    } finally {
      syncing = false;
      if (pendingResync) {
        pendingResync = false;
        recalculate();
      }
    }
  }

  document.addEventListener('shopify:cart:lines-update', function (event) {
    if (event.__surchargeSync) return;
    // The dispatching component fires this event before its cart mutation
    // request resolves (event.promise settles later). Recalculating now would
    // read stale /cart.js state, so wait for the mutation to land first.
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(recalculate, recalculate);
    } else {
      recalculate();
    }
  });

  // Catch any pre-existing mismatch (e.g. cart state from a previous session or tab).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recalculate);
  } else {
    recalculate();
  }
})();
